import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from 'react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminSeasonsAPI, adminCommunicationsAPI } from '../services/adminApi';
import AdminPlayerHomeBanners from './AdminPlayerHomeBanners';
import AdminBulkEmail from './AdminBulkEmail';
import EmailAttachments from './EmailAttachments';

const Container = styled.div`
  padding: 1rem;
  max-width: 800px;
`;
const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;
const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;
const Lead = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.1rem;
`;
const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;
const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  max-width: 100%;
`;
const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
`;
const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const RadioRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #1f2937;
  input {
    margin-top: 0.2rem;
  }
`;
const Hint = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  button {
    border: none;
    background: transparent;
    cursor: pointer;
    color: #4f46e5;
    font-size: 1rem;
    line-height: 1;
  }
`;
const AddRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;
const BtnGhost = styled.button`
  background: #fff;
  color: #043663;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewBox = styled.div`
  margin-top: 1rem;
  padding: 1rem 1.1rem;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  background: #f0f9ff;
  font-size: 0.875rem;
  color: #0f172a;
`;

const TeamPreviewList = styled.ul`
  margin: 0.65rem 0 0;
  padding-left: 1.1rem;
  max-height: 220px;
  overflow-y: auto;
  li {
    margin-bottom: 0.35rem;
  }
`;

const DivisionPreviewBlock = styled.div`
  margin-top: 0.75rem;
  &:first-of-type {
    margin-top: 0.35rem;
  }
`;

const DivisionPreviewTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.35rem;
`;

const DivisionPreviewList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  li {
    margin-bottom: 0.3rem;
    font-size: 0.82rem;
    color: #334155;
  }
`;

const Btn = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const QuillWrap = styled.div`
  .ql-container {
    min-height: 220px;
    font-size: 0.95rem;
  }
  .ql-editor {
    min-height: 200px;
  }
`;

const ProgressCard = styled.div`
  margin-top: 1.25rem;
  padding: 1.1rem 1.25rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.06);
`;
const ProgressBarOuter = styled.div`
  height: 10px;
  background: #e0f2fe;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 0.5rem;
`;
const ProgressBarInner = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #043663, #0ea5e9);
  width: ${(p) => p.$pct}%;
  transition: width 0.3s ease;
`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
`;
const StatBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  small {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.02em;
  }
  strong {
    font-size: 1.15rem;
    color: #0f172a;
  }
`;
const ErrorList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.1rem;
  font-size: 0.8rem;
  color: #b91c1c;
`;

const CommTabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;
const CommTabBtn = styled.button`
  border: 1px solid #e5e7eb;
  background: ${(p) => (p.$active ? '#043663' : '#ffffff')};
  color: ${(p) => (p.$active ? '#ffffff' : '#374151')};
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: #cbd5e1;
  }
`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

function groupLeaderboardTeamsByDivision(teams) {
  if (!Array.isArray(teams) || teams.length === 0) return [];
  const byDivision = new Map();
  for (const t of teams) {
    const key = t.division_name || 'Unknown division';
    if (!byDivision.has(key)) byDivision.set(key, []);
    byDivision.get(key).push(t);
  }
  return [...byDivision.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([divisionName, list]) => ({
      divisionName,
      teams: [...list].sort(
        (a, b) =>
          (Number(a.leaderboard_position) || 999) - (Number(b.leaderboard_position) || 999) ||
          String(a.team_name || '').localeCompare(String(b.team_name || ''))
      ),
    }));
}

function renderTeamPreviewMeta(t, audiencePreview) {
  const parts = [];
  if (audiencePreview.audienceType === 'season') {
    parts.push(`${t.matches_played ?? 0} completed match(es)`);
  }
  if (t.is_active === false) parts.push('disabled');
  if (t.partner_communications) parts.push('partner consent ✓');
  if (t.email) parts.push(t.email);
  return parts.length ? ` — ${parts.join(' · ')}` : '';
}

/**
 * @param {{ canSend: boolean }} props
 */
const AdminSettingsEmail = ({ canSend = false }) => {
  const { adminUser } = useAdminAuth();
  const [commTab, setCommTab] = useState('email');
  const { data: seasonsRes } = useQuery('admin-seasons-email', () => adminSeasonsAPI.getAll(), {
    enabled: !!adminUser,
    retry: false,
  });
  const list = Array.isArray(seasonsRes?.data) ? seasonsRes.data : [];

  const [seasonId, setSeasonId] = useState('');
  const [recipientMode, setRecipientMode] = useState('team_only');
  const [audienceType, setAudienceType] = useState('season');
  const [scope, setScope] = useState('all_teams');
  const [matchCountFilterEnabled, setMatchCountFilterEnabled] = useState(false);
  const [matchCountFilter, setMatchCountFilter] = useState('0');
  const [includeDisabledTeams, setIncludeDisabledTeams] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [attachSessionId, setAttachSessionId] = useState(null);
  const [attachFiles, setAttachFiles] = useState([]);

  const [activeJobId, setActiveJobId] = useState(null);
  const [jobSnapshot, setJobSnapshot] = useState(null);
  const [completedSummary, setCompletedSummary] = useState(null);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const doSearch = useCallback(async () => {
    if (!seasonId) {
      toast.error('Choose a phase first');
      return;
    }
    const q = searchQ.trim();
    if (q.length < 2) {
      toast.error('Type at least 2 characters to search');
      return;
    }
    try {
      const res = await adminCommunicationsAPI.teamSearch({ season_id: seasonId, q });
      if (res?.success && res.data?.teams) {
        setSearchResults(res.data.teams);
        if (res.data.teams.length === 0) {
          toast('No teams found', { icon: 'ℹ️' });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Search failed');
    }
  }, [seasonId, searchQ]);

  const addTeam = (t) => {
    if (selectedTeams.some((x) => x.id === t.id)) return;
    setSelectedTeams((prev) => [...prev, { id: t.id, team_name: t.team_name, email: t.email }]);
    setSearchResults([]);
    setSearchQ('');
  };
  const removeTeam = (id) => {
    setSelectedTeams((prev) => prev.filter((x) => x.id !== id));
  };

  const isPartnerSubscribers = audienceType === 'partner_subscribers';

  const buildAudiencePayload = () => ({
    seasonId: isPartnerSubscribers ? undefined : seasonId,
    recipientMode,
    scope: isPartnerSubscribers ? 'partner_subscribers' : scope,
    teamIds: !isPartnerSubscribers && scope === 'selected_teams' ? selectedTeams.map((t) => t.id) : undefined,
    matchCountFilter:
      !isPartnerSubscribers && matchCountFilterEnabled && matchCountFilter !== ''
        ? Number.parseInt(matchCountFilter, 10)
        : null,
    includeDisabledTeams: isPartnerSubscribers ? includeDisabledTeams : false,
  });

  const canPreviewAudience = useMemo(
    () =>
      (isPartnerSubscribers ||
        (seasonId &&
          (scope === 'all_teams' ||
            scope === 'leaderboard_top_promoted' ||
            selectedTeams.length > 0))) &&
      (!matchCountFilterEnabled ||
        isPartnerSubscribers ||
        (Number.isInteger(Number.parseInt(matchCountFilter, 10)) &&
          Number.parseInt(matchCountFilter, 10) >= 0)),
    [
      isPartnerSubscribers,
      seasonId,
      scope,
      selectedTeams.length,
      matchCountFilterEnabled,
      matchCountFilter,
    ]
  );

  const runPreview = useCallback(async () => {
    if (!canPreviewAudience) return;
    setPreviewLoading(true);
    setAudiencePreview(null);
    try {
      const res = await adminCommunicationsAPI.broadcastPreview(buildAudiencePayload());
      if (res?.success && res.data) {
        setAudiencePreview(res.data);
      } else {
        toast.error(res?.message || 'Preview failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [canPreviewAudience, seasonId, recipientMode, scope, selectedTeams, matchCountFilterEnabled, matchCountFilter, audienceType, includeDisabledTeams]);

  const sendMutation = useMutation(
    () =>
      adminCommunicationsAPI.broadcastEmail({
        ...buildAudiencePayload(),
        subject: subject.trim(),
        htmlBody,
        attachmentSessionId: attachSessionId || undefined,
      }),
    {
      onSuccess: (res) => {
        if (res?.success && res.data) {
          setCompletedSummary(null);
          setAttachSessionId(null);
          setAttachFiles([]);
          if (res.data.async && res.data.jobId) {
            setActiveJobId(res.data.jobId);
            setJobSnapshot({
              status: 'sending',
              jobId: res.data.jobId,
              total: res.data.uniqueRecipients,
              processed: 0,
              delivered: 0,
              sentFailed: 0,
              ...res.data,
            });
            return;
          }
          setActiveJobId(null);
          setJobSnapshot(null);
          setCompletedSummary(res.data);
          const { delivered, total, sentFailed, replyToUsed } = res.data;
          toast.success(
            `Done: ${delivered ?? 0} delivered of ${total ?? 0} message(s)${
              sentFailed ? ` (${sentFailed} failed)` : ''
            }${replyToUsed ? `. Replies: ${replyToUsed}` : ''}`
          );
        } else {
          toast.error(res?.message || 'Send failed');
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || err.message || 'Send failed');
      },
    }
  );

  useEffect(() => {
    if (!activeJobId) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await adminCommunicationsAPI.getBroadcastJob(activeJobId);
        if (cancelled || !res?.success || !res.data) return;
        setJobSnapshot(res.data);
        if (res.data.status === 'done' || res.data.status === 'error') {
          setCompletedSummary(res.data);
          setJobSnapshot(null);
          setActiveJobId(null);
          if (res.data.status === 'done') {
            toast.success(
              `Done: ${res.data.delivered ?? 0} delivered, ${res.data.sentFailed ?? 0} failed (of ${
                res.data.total
              } recipients)`
            );
          } else {
            toast.error(res.data.errorMessage || 'Send failed before completion');
          }
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setActiveJobId(null);
          setJobSnapshot(null);
          toast.error('Send status expired. Check server logs for the final result.');
        }
      }
    };

    tick();
    const id = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeJobId]);

  useEffect(() => {
    setAudiencePreview(null);
  }, [seasonId, recipientMode, scope, selectedTeams, matchCountFilterEnabled, matchCountFilter, audienceType, includeDisabledTeams]);

  const canSubmit = useMemo(
    () =>
      canSend &&
      !sendMutation.isLoading &&
      !activeJobId &&
      (isPartnerSubscribers || seasonId) &&
      subject.trim() &&
      htmlBody &&
      String(htmlBody).replace(/<[^>]+>/g, '').trim() &&
      (isPartnerSubscribers ||
        scope === 'all_teams' ||
        scope === 'leaderboard_top_promoted' ||
        selectedTeams.length > 0) &&
      (!matchCountFilterEnabled ||
        isPartnerSubscribers ||
        (Number.isInteger(Number.parseInt(matchCountFilter, 10)) &&
          Number.parseInt(matchCountFilter, 10) >= 0)),
    [
      canSend,
      sendMutation.isLoading,
      activeJobId,
      isPartnerSubscribers,
      seasonId,
      subject,
      htmlBody,
      scope,
      selectedTeams.length,
      matchCountFilterEnabled,
      matchCountFilter,
    ]
  );

  return (
    <Container>
      <CommTabRow role="tablist" aria-label="Partner communications">
        <CommTabBtn
          type="button"
          role="tab"
          aria-selected={commTab === 'email'}
          $active={commTab === 'email'}
          onClick={() => setCommTab('email')}
        >
          Email broadcast
        </CommTabBtn>
        <CommTabBtn
          type="button"
          role="tab"
          aria-selected={commTab === 'bulk'}
          $active={commTab === 'bulk'}
          onClick={() => setCommTab('bulk')}
        >
          Bulk email
        </CommTabBtn>
        <CommTabBtn
          type="button"
          role="tab"
          aria-selected={commTab === 'banners'}
          $active={commTab === 'banners'}
          onClick={() => setCommTab('banners')}
        >
          Player home banners
        </CommTabBtn>
      </CommTabRow>

      {commTab === 'banners' ? (
        <AdminPlayerHomeBanners canEdit={canSend} />
      ) : commTab === 'bulk' ? (
        <AdminBulkEmail canSend={canSend} />
      ) : (
        <>
      <Card>
        <Title>Email</Title>
        <Lead>
          Choose <strong>phase teams</strong> for league updates, or <strong>partner communication subscribers</strong>{' '}
          for partner/newsletter-style mail (opt-in at registration, not tied to a phase). Phase sends only include
          teams registered in that phase; disabled teams are excluded unless you opt in below for partner subscribers.{' '}
          <strong>Inactive members</strong> (no email or inactive) are not mailed. Set{' '}
          <code>HPS_REPLY_TO_EMAIL</code> (or <code>EMAIL_REPLY_TO</code>) for replies.
        </Lead>

        <Field>
          <Label>Audience type</Label>
          <RadioGroup>
            <RadioRow>
              <input
                type="radio"
                name="audienceType"
                checked={audienceType === 'season'}
                onChange={() => setAudienceType('season')}
              />
              <span>Teams in a phase (league / operational emails)</span>
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="audienceType"
                checked={audienceType === 'partner_subscribers'}
                onChange={() => setAudienceType('partner_subscribers')}
              />
              <span>Partner communication subscribers (opt-in newsletter list, all phases)</span>
            </RadioRow>
          </RadioGroup>
        </Field>

        {!isPartnerSubscribers && (
        <Field>
          <Label>Phase</Label>
          <Select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
            <option value="">Select phase</option>
            {list.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
              </option>
            ))}
          </Select>
        </Field>
        )}

        <Field>
          <Label>Send to</Label>
          <RadioGroup>
            <RadioRow>
              <input
                type="radio"
                name="recip"
                checked={recipientMode === 'team_only'}
                onChange={() => setRecipientMode('team_only')}
              />
              <span>Team contact email only (one email per team)</span>
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="recip"
                checked={recipientMode === 'members_only'}
                onChange={() => setRecipientMode('members_only')}
              />
              <span>Each member individually (active members with an email on file)</span>
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="recip"
                checked={recipientMode === 'team_and_members'}
                onChange={() => setRecipientMode('team_and_members')}
              />
              <span>Team email and each member (duplicates to the same address are only sent once)</span>
            </RadioRow>
          </RadioGroup>
        </Field>

        {!isPartnerSubscribers ? (
        <Field>
          <Label>Phase audience</Label>
          <RadioGroup>
            <RadioRow>
              <input
                type="radio"
                name="scope"
                checked={scope === 'all_teams'}
                onChange={() => setScope('all_teams')}
              />
              <span>All teams registered in this phase</span>
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="scope"
                checked={scope === 'selected_teams'}
                onChange={() => setScope('selected_teams')}
              />
              <span>Only specific teams (add by name below)</span>
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="scope"
                checked={scope === 'leaderboard_top_promoted'}
                onChange={() => setScope('leaderboard_top_promoted')}
              />
              <span>Top 2 on each division leaderboard</span>
            </RadioRow>
          </RadioGroup>
          {scope === 'leaderboard_top_promoted' ? (
            <Hint style={{ marginTop: '0.5rem' }}>
              Active teams ranked <strong>1 and 2</strong> in each division for the selected phase (including split groups A/B/C). Parent divisions are hidden when split. Disabled teams are excluded.
            </Hint>
          ) : null}
        </Field>
        ) : (
        <Field>
          <Label>Partner subscribers</Label>
          <Hint style={{ marginBottom: '0.65rem' }}>
            All teams in your tenant who ticked <strong>partner communications</strong> at registration — active or past
            phases. No phase selection needed (like a newsletter list).
          </Hint>
          <RadioRow>
            <input
              type="checkbox"
              id="include-disabled-partner"
              checked={includeDisabledTeams}
              onChange={(e) => setIncludeDisabledTeams(e.target.checked)}
            />
            <span htmlFor="include-disabled-partner">Include disabled teams (globally turned off)</span>
          </RadioRow>
          <Hint>
            By default only <strong>active</strong> teams with partner consent are included. Check this to also mail
            disabled teams that still opted in.
          </Hint>
        </Field>
        )}

        {!isPartnerSubscribers && (
        <Field>
          <Label>Matches played in this phase (optional)</Label>
          <RadioRow style={{ marginBottom: '0.65rem' }}>
            <input
              type="checkbox"
              id="match-count-filter"
              checked={matchCountFilterEnabled}
              onChange={(e) => setMatchCountFilterEnabled(e.target.checked)}
            />
            <span htmlFor="match-count-filter">Filter by completed matches in the selected phase</span>
          </RadioRow>
          {matchCountFilterEnabled && (
            <AddRow>
              <Label style={{ margin: 0, whiteSpace: 'nowrap' }}>Exactly</Label>
              <Input
                type="number"
                min={0}
                max={99}
                value={matchCountFilter}
                onChange={(e) => setMatchCountFilter(e.target.value)}
                style={{ width: 72 }}
              />
              <span style={{ fontSize: '0.9rem', color: '#374151' }}>completed match(es)</span>
            </AddRow>
          )}
          <Hint>
            When enabled, only teams with that many <strong>completed</strong> league matches in this phase are
            included. Combines with phase audience and recipient options above.
          </Hint>
        </Field>
        )}

        {!isPartnerSubscribers && scope === 'selected_teams' && (
          <Field>
            <Label>Add teams</Label>
            <AddRow>
              <Input
                type="search"
                placeholder="Type team name…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
                style={{ minWidth: 200, flex: 1 }}
              />
              <Btn type="button" onClick={doSearch} disabled={!seasonId}>
                Search
              </Btn>
            </AddRow>
            {searchResults.length > 0 && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 160, overflow: 'auto' }}>
                {searchResults.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => addTeam(t)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem 0.75rem',
                      border: 'none',
                      borderBottom: '1px solid #f3f4f6',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {t.team_name} {t.email ? `— ${t.email}` : ''}
                  </button>
                ))}
              </div>
            )}
            {selectedTeams.length > 0 && (
              <ChipRow>
                {selectedTeams.map((t) => (
                  <Chip key={t.id}>
                    {t.team_name}
                    <button type="button" onClick={() => removeTeam(t.id)} aria-label="Remove">
                      ×
                    </button>
                  </Chip>
                ))}
              </ChipRow>
            )}
            <Hint>Search requires at least 2 characters. Click a row to add. Remove with ×.</Hint>
          </Field>
        )}

        <AddRow style={{ marginBottom: '1rem' }}>
          <BtnGhost type="button" onClick={runPreview} disabled={!canPreviewAudience || previewLoading}>
            {previewLoading ? 'Checking audience…' : 'Preview audience (no emails sent)'}
          </BtnGhost>
        </AddRow>

        {audiencePreview ? (
          <PreviewBox>
            <strong>
              {audiencePreview.teamsInScope} team(s) → {audiencePreview.uniqueRecipients} unique recipient
              {audiencePreview.uniqueRecipients === 1 ? '' : 's'}
            </strong>
            {audiencePreview.audienceType === 'partner_subscribers' ? (
              <div style={{ marginTop: 6, color: '#0369a1' }}>
                Partner subscribers (all phases)
                {audiencePreview.includeDisabledTeams ? ' · including disabled teams' : ' · active teams only'}
              </div>
            ) : null}
            {audiencePreview.matchCountFilter != null ? (
              <div style={{ marginTop: 6, color: '#0369a1' }}>
                Match filter: exactly {audiencePreview.matchCountFilter} completed match(es) in phase
              </div>
            ) : null}
            {audiencePreview.leaderboardTopPromoted ? (
              <div style={{ marginTop: 6, color: '#0369a1' }}>
                Leaderboard filter: top 2 teams in each division (ranks 1 and 2)
              </div>
            ) : null}
            {audiencePreview.audienceType !== 'partner_subscribers' &&
            audiencePreview.partnerCommunicationsOnly ? (
              <div style={{ marginTop: 6, color: '#0369a1' }}>
                Partner consent filter: only teams that opted in during registration
              </div>
            ) : null}
            {Array.isArray(audiencePreview.sampleRecipients) && audiencePreview.sampleRecipients.length > 0 ? (
              <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#475569' }}>
                Sample addresses: {audiencePreview.sampleRecipients.join(', ')}
                {audiencePreview.uniqueRecipients > audiencePreview.sampleRecipients.length ? ' …' : ''}
              </div>
            ) : null}
            {Array.isArray(audiencePreview.teams) && audiencePreview.teams.length > 0 ? (
              <>
                <div style={{ marginTop: 10, fontWeight: 600 }}>Teams included</div>
                {audiencePreview.leaderboardTopPromoted ? (
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {groupLeaderboardTeamsByDivision(audiencePreview.teams).map(({ divisionName, teams }) => (
                      <DivisionPreviewBlock key={divisionName}>
                        <DivisionPreviewTitle>{divisionName}</DivisionPreviewTitle>
                        <DivisionPreviewList>
                          {teams.map((t) => (
                            <li key={t.id}>
                              <strong>#{t.leaderboard_position ?? '?'}</strong> {t.team_name}
                              {renderTeamPreviewMeta(t, audiencePreview)}
                            </li>
                          ))}
                        </DivisionPreviewList>
                      </DivisionPreviewBlock>
                    ))}
                  </div>
                ) : (
                  <TeamPreviewList>
                    {audiencePreview.teams.map((t) => (
                      <li key={t.id}>
                        {t.team_name}
                        {audiencePreview.audienceType === 'season'
                          ? ` — ${t.matches_played ?? 0} completed match(es)`
                          : ''}
                        {t.is_active === false ? ' · disabled' : ''}
                        {t.partner_communications ? ' · partner consent ✓' : ''}
                        {t.email ? ` · ${t.email}` : ''}
                      </li>
                    ))}
                  </TeamPreviewList>
                )}
              </>
            ) : null}
          </PreviewBox>
        ) : null}

        <Field>
          <Label>Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" />
        </Field>

        <Field>
          <Label>Message</Label>
          <QuillWrap>
            <ReactQuill
              theme="snow"
              value={htmlBody}
              onChange={setHtmlBody}
              modules={quillModules}
              placeholder="Write your message…"
            />
          </QuillWrap>
          <Hint>
            Your text is placed inside the same Helvetic Padel email layout as other system messages: logo, header, and
            footer, plus a &quot;Go to Dashboard&quot; button (logo loads from the live site so it appears in inboxes).
          </Hint>
        </Field>

        <EmailAttachments
          disabled={!canSend || sendMutation.isLoading || !!activeJobId}
          sessionId={attachSessionId}
          onSessionChange={setAttachSessionId}
          files={attachFiles}
          onFilesChange={setAttachFiles}
        />

        <Btn
          type="button"
          onClick={() => {
            setCompletedSummary(null);
            sendMutation.mutate();
          }}
          disabled={!canSubmit || sendMutation.isLoading}
        >
          {sendMutation.isLoading
            ? 'Starting send…'
            : activeJobId
              ? 'Send in progress…'
              : 'Send email'}
        </Btn>
        {!canSend && (
          <Hint style={{ marginTop: 12, color: '#b91c1c' }}>
            You do not have permission to send (requires Settings — edit).
          </Hint>
        )}
      </Card>

      {activeJobId && jobSnapshot && !completedSummary ? (
        <ProgressCard>
          <Title style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>Sending in progress</Title>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#0369a1' }}>
            {jobSnapshot?.total
              ? `Processing message ${jobSnapshot.processed || 0} of ${jobSnapshot.total} (unique addresses). You can
              leave this page; progress updates every few seconds.`
              : 'Queued. Connecting to status…'}
          </p>
          {!!jobSnapshot?.total && (
            <>
              <ProgressBarOuter>
                <ProgressBarInner
                  $pct={Math.min(100, ((jobSnapshot.processed || 0) / jobSnapshot.total) * 100)}
                />
              </ProgressBarOuter>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', marginTop: 6 }}>
                <span>Delivered: {jobSnapshot.delivered ?? 0}</span>
                <span>Failed: {jobSnapshot.sentFailed ?? 0}</span>
              </div>
            </>
          )}
        </ProgressCard>
      ) : null}

      {completedSummary && (
        <Card style={{ marginTop: '1rem' }}>
          <Title style={{ fontSize: '1.1rem' }}>Send results</Title>
          {completedSummary.status === 'error' && (
            <p style={{ color: '#b91c1c', fontSize: '0.9rem' }}>{completedSummary.errorMessage || 'Error'}</p>
          )}
          <StatGrid>
            <StatBox>
              <small>Audience</small>
              <strong style={{ fontSize: '0.85rem' }}>
                {completedSummary.audienceType === 'partner_subscribers'
                  ? 'Partner subscribers'
                  : 'Phase teams'}
              </strong>
            </StatBox>
            <StatBox>
              <small>Teams in scope</small>
              <strong>{completedSummary.teamsInScope ?? '—'}</strong>
            </StatBox>
            <StatBox>
              <small>Unique recipients</small>
              <strong>{completedSummary.uniqueRecipients ?? completedSummary.total ?? '—'}</strong>
            </StatBox>
            <StatBox>
              <small>Delivered</small>
              <strong style={{ color: '#15803d' }}>{completedSummary.delivered ?? 0}</strong>
            </StatBox>
            <StatBox>
              <small>Failed</small>
              <strong style={{ color: completedSummary.sentFailed > 0 ? '#b91c1c' : '#64748b' }}>
                {completedSummary.sentFailed ?? 0}
              </strong>
            </StatBox>
            <StatBox>
              <small>Match filter</small>
              <strong style={{ fontSize: '0.85rem' }}>
                {completedSummary.matchCountFilter != null
                  ? `Exactly ${completedSummary.matchCountFilter} completed`
                  : 'None'}
              </strong>
            </StatBox>
            <StatBox>
              <small>Partner consent</small>
              <strong style={{ fontSize: '0.85rem' }}>
                {completedSummary.audienceType === 'partner_subscribers'
                  ? 'Subscribers only'
                  : completedSummary.partnerCommunicationsOnly
                    ? 'Consented teams only'
                    : 'All teams'}
              </strong>
            </StatBox>
            <StatBox>
              <small>Disabled teams</small>
              <strong style={{ fontSize: '0.85rem' }}>
                {completedSummary.audienceType === 'partner_subscribers'
                  ? completedSummary.includeDisabledTeams
                    ? 'Included'
                    : 'Excluded'
                  : 'Excluded (always)'}
              </strong>
            </StatBox>
            <StatBox>
              <small>Reply-To</small>
              <strong style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {completedSummary.replyToUsed || '—'}
              </strong>
            </StatBox>
          </StatGrid>
          {Array.isArray(completedSummary.sampleErrors) && completedSummary.sampleErrors.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <Label style={{ color: '#b91c1c' }}>Sample errors (up to 25)</Label>
              <ErrorList>
                {completedSummary.sampleErrors.map((e, i) => (
                  <li key={i}>
                    {e.to}: {e.message}
                  </li>
                ))}
              </ErrorList>
            </div>
          )}
        </Card>
      )}
        </>
      )}
    </Container>
  );
};

export default AdminSettingsEmail;
